/* eslint-disable react/prop-types */
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Spin,
  message,
} from 'antd';
import {
  deleteLimitacionAutorizacionContrataciones,
  getLimitacionesAutorizacionContrataciones,
  patchLimitacionAutorizacionContrataciones,
  postLimitacionAutorizacionContrataciones,
} from 'api/usuarios/limitacionAutorizacionContrataciones';
import { getUsuariosLimitaciones } from 'api/usuarios/usuariosLimitaciones';
import { getUsuarios } from 'api/usuarios/usuarios';
import { getPuestos } from 'api/catalogos/puestos';
import { getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalConfirmation from 'components/ModalConfirmation';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import Table from 'components/Table';
import React, { useEffect, useState } from 'react';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { DATE_FRONTEND_FORMAT } from 'utils/normalizers';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';

export const limitacionAutorizacionContratacionesPermission = {
  permissionModel: 'usuariolimitacionautorizacioncontrataciones',
};

const LimitacionAutorizacionContrataciones = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [usuariosLimitacionOptions, setUsuariosLimitacionOptions] = useState([]);
  const [disableFields, setDisableFields] = useState(false);
  const [confirmationVisibleAutorizar, setConfirmationVisibleAutorizar] = useState(false);
  const [confirmationVisibleCancelar, setConfirmationVisibleCancelar] = useState(false);
  const [confirmationVisibleReactivar, setConfirmationVisibleReactivar] = useState(false);

  const buildUsuariosLimitacionOptions = (usuariosLimitacion, usuarios) => usuariosLimitacion.map(
    (uli) => ({
      id: uli.id,
      descripcion: usuarios.find((u) => u.id === uli.usuario)?.email || `Usuario Límite #${uli.id}`,
    }),
  );

  const fetchData = async () => {
    setLoading(true);
    const _data = await getLimitacionesAutorizacionContrataciones();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _puestos,
        _data,
        _usuariosLimitacion,
        _usuarios,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPuestos(),
        getLimitacionesAutorizacionContrataciones(),
        getUsuariosLimitaciones(),
        getUsuarios(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setPuestos(_puestos);
        setData(formatReceived(_data));
        setUsuariosLimitacionOptions(
          buildUsuariosLimitacionOptions(_usuariosLimitacion, _usuarios),
        );
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    fetchData();
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data?.find((item) => item.id === key);
    setTimeout(() => {
      if (match) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(match));
          // Un paso Autorizado o Cancelado ya no debe poder editarse, salvo su
          // Vigencia Final (para poder cerrarle vigencia sin reabrir el paso).
          setDisableFields(
            match.estados_globales === ESTATUS_AUTORIZADO
            || match.estados_globales === ESTATUS_CANCELADO,
          );
          setVisible(true);
        });
      } else {
        message.warn('Este registro no es editable');
      }
    });
    setLoading(false);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;

      if (values.vigencia_final && typeof values.vigencia_final.format === 'function') {
        values.vigencia_final = values.vigencia_final.format('YYYY-MM-DD');
      }

      const saved = await (
        key
          ? patchLimitacionAutorizacionContrataciones(key, values)
          : postLimitacionAutorizacionContrataciones(values)
      );
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteLimitacionAutorizacionContrataciones(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const cambiarEstatus = async (estados_globales, onDone) => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const res = await patchLimitacionAutorizacionContrataciones(key, { estados_globales });
      if (res) {
        await fetchData();
        onDone();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationAutorizar = () => cambiarEstatus(
    ESTATUS_AUTORIZADO,
    () => setConfirmationVisibleAutorizar(false),
  );

  const handleOnFinishConfirmationCancelar = () => cambiarEstatus(
    ESTATUS_CANCELADO,
    () => setConfirmationVisibleCancelar(false),
  );

  const handleOnFinishConfirmationReactivar = () => cambiarEstatus(
    ESTATUS_ACTIVO,
    () => setConfirmationVisibleReactivar(false),
  );

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  // Resuelve una lista de IDs de UsuarioLimitacionDeInformacion a los
  // correos de los usuarios del sistema que representan (mismo catálogo
  // que usa el formulario de Agregar/Editar).
  const resolverUsuarios = (ids) => (ids || [])
    .map((id) => usuariosLimitacionOptions.find((o) => o.id === id)?.descripcion)
    .filter(Boolean)
    .join(', ') || '-';

  const columns = [
    {
      titleText: 'Paso',
      dataIndex: 'paso',
      key: 'paso',
      width: 100,
    },
    {
      titleText: 'Botón',
      dataIndex: 'nombre_boton',
      key: 'nombre_boton',
      width: 250,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto',
      key: 'puesto',
      width: 200,
      render: (val) => puestos.find((p) => p.id === val)?.descripcion || 'N/A',
    },
    {
      titleText: 'Usuarios que Autorizan',
      dataIndex: 'usuarios_limitacion_de_informacion',
      key: 'usuarios_limitacion_de_informacion',
      width: 250,
      render: (val) => resolverUsuarios(val),
    },
    {
      titleText: 'Depende de',
      dataIndex: 'depende_de_usuarios_limitacion_de_informacion',
      key: 'depende_de_usuarios_limitacion_de_informacion',
      width: 250,
      render: (val) => resolverUsuarios(val),
    },
    {
      titleText: 'Visto Bueno',
      dataIndex: 'visto_bueno_autorizacion',
      key: 'visto_bueno_autorizacion',
      width: 150,
      render: (val) => (val ? 'Sí' : 'No'),
    },
    {
      titleText: 'Requiere Observaciones',
      dataIndex: 'requiere_observaciones',
      key: 'requiere_observaciones',
      width: 150,
      render: (val) => (val ? 'Sí' : 'No'),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 200,
      render: (val) => (val ? val?.format(DATE_FRONTEND_FORMAT) : 'N/A'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableDelete={
            data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
            || data?.find((e) => e?.id === selectedRowKeys[0])
              ?.estados_globales === ESTATUS_INACTIVO
          }
          customActions={[
            {
              onClick: () => setConfirmationVisibleAutorizar(true),
              permission: { model: limitacionAutorizacionContratacionesPermission.permissionModel, action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length
                || data?.find((e) => e?.id === selectedRowKeys[0])
                  ?.estados_globales === ESTATUS_AUTORIZADO
                || data?.find((e) => e?.id === selectedRowKeys[0])
                  ?.estados_globales === ESTATUS_CANCELADO
                || data?.find((e) => e?.id === selectedRowKeys[0])
                  ?.estados_globales === ESTATUS_INACTIVO,
            },
            {
              onClick: () => setConfirmationVisibleCancelar(true),
              permission: { model: limitacionAutorizacionContratacionesPermission.permissionModel, action: 'cancelar' },
              text: 'Cancelar',
              icon: <CloseCircleOutlined />,
              disabledBySelection: false,
              className: 'cancel',
              disabled: !selectedRowKeys?.length
                || data?.find((e) => e?.id === selectedRowKeys[0])
                  ?.estados_globales === ESTATUS_CANCELADO,
            },
            {
              onClick: () => setConfirmationVisibleReactivar(true),
              permission: { model: limitacionAutorizacionContratacionesPermission.permissionModel, action: 'reactivar' },
              text: 'Reactivar',
              icon: <RedoOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length
                || data?.find((e) => e?.id === selectedRowKeys[0])
                  ?.estados_globales === ESTATUS_ACTIVO,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Paso de Autorización de Contratación`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
              requiere_observaciones: false,
              visto_bueno_autorizacion: false,
            }}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="paso"
                  label="Paso"
                  rules={rules.required}
                  hasFeedback
                >
                  <InputNumber style={{ width: '100%' }} disabled={disableFields} min={1} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={16}>
                <Form.Item
                  name="nombre_boton"
                  label="Texto del Botón"
                  rules={rules.required}
                  hasFeedback
                >
                  <Input disabled={disableFields} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="descripcion"
                  label="Descripción"
                  rules={rules.required}
                  hasFeedback
                >
                  <Input disabled={disableFields} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="puesto"
                  label="Puesto"
                  hasFeedback
                >
                  <Select
                    dataSource={puestos}
                    disabled={disableFields}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="requiere_observaciones"
                  label="Requiere Observaciones"
                  hasFeedback
                  rules={rules.required}
                >
                  <Select trueFalse disabled={disableFields} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="visto_bueno_autorizacion"
                  label="Visto Bueno (finaliza autorización)"
                  hasFeedback
                  rules={rules.required}
                >
                  <Select trueFalse disabled={disableFields} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="vigencia_final"
                  label="Vigencia Final"
                  hasFeedback
                >
                  {/* Único campo editable aun con el paso Autorizado/Cancelado
                      (ver disableFields en onClickEdit) — permite cerrarle
                      vigencia sin tener que reactivar el paso. */}
                  <DatePicker format={DATE_FRONTEND_FORMAT} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="usuarios_limitacion_de_informacion"
                  label="Usuarios que Autorizan este Paso"
                  hasFeedback
                >
                  <Select
                    mode="multiple"
                    dataSource={usuariosLimitacionOptions}
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="depende_de_usuarios_limitacion_de_informacion"
                  label="Depende de la Autorización de"
                  hasFeedback
                >
                  <Select
                    mode="multiple"
                    dataSource={usuariosLimitacionOptions}
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="El registro seleccionado"
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationAutorizar}
          onFinishText="Continuar"
          onCancel={() => setConfirmationVisibleAutorizar(false)}
          open={confirmationVisibleAutorizar}
          title="Confirmación"
          contentTitle={`autorizar el paso "${data?.find((e) => e?.id === selectedRowKeys[0])?.nombre_boton || ''}"`}
          ConfirmAutorize
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationCancelar}
          onFinishText="Continuar"
          onCancel={() => setConfirmationVisibleCancelar(false)}
          open={confirmationVisibleCancelar}
          title="Confirmación"
          contentTitle={`cancelar el paso "${data?.find((e) => e?.id === selectedRowKeys[0])?.nombre_boton || ''}"`}
          ConfirmCancel
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => setConfirmationVisibleReactivar(false)}
          open={confirmationVisibleReactivar}
          title="Confirmación"
          contentTitle={`reactivar el paso "${data?.find((e) => e?.id === selectedRowKeys[0])?.nombre_boton || ''}"`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default LimitacionAutorizacionContrataciones;
